import { useEffect, useRef, useState } from "react";
import { R } from "../resources";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
import "../styles/page-home.css";

const CTA_LABEL = "Begin a partnership";

const HEADLINE = {
  h: <>Qualified leads.<br />Fixed cost.<br /><span className="line2"><em>End-to-end.</em></span></>,
  sub: "We invest in your marketing. You pay for what works. The kind of arrangement worth calling a partnership."
};



/* ──────── nav ──────── */



/* ──────── hero ──────── */

function Hero({ headline, ctaLabel, onCta }) {
  return (
    <section className="hero" id="top" data-screen-label="01 Hero">
      <div className="wrap">
        <div className="hero-headline-wrap">
          <div className="hero-meta">
            <span>GLOBAL MEDIA, CREATIVE, CONVERSION, DATA, AND TECHNOLOGY EXPERTS – SINCE 2009</span>
          </div>
          <h1 className="hero-headline" data-comment-anchor="hero-headline">{headline.h}</h1>
        </div>
        <div className="hero-bottom">
          <p className="hero-sub">{headline.sub}</p>
          <div className="hero-cta-stack">
            <div className="hero-cta-row">
              <button className="link-btn-primary" onClick={onCta}>
                {ctaLabel} <span className="arrow">→</span>
              </button>
              <a className="link-btn" href="#how">See how it works <span className="arrow">↓</span></a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ──────── how it works ──────── */

function HowItWorks() {
  const stages = [
  {
    n: "01",
    h: "Multi-Channel Acquisition",
    p: "We monitor buyer behaviour across Google, Meta, TikTok, Microsoft, and native channels using our proprietary systems. Our data-driven process identifies high-intent audiences at the point they're ready to buy, directing our $50M+ annual budget to where the return is highest and reallocating as the data shifts."
  },
  {
    n: "02",
    h: "Creative and Conversion",
    p: "We build the funnels using conversion logic developed across many active brands and verticals. Creative direction and CRO specialists calibrate each layer to your ideal customer. Targeting and qualification are configured to your standards, filtering for prospects worth your sales team's time."
  },
  {
    n: "03",
    h: "Real-time Lead Validation",
    p: "Every lead runs through our validation system before delivery. Internal scrubbing and scoring happens in milliseconds, combined with checks from external enrichment platforms to confirm contactability and lead quality. The person on the other end is real, reachable, and worth the call."
  },
  {
    n: "04",
    h: "Live Sales Delivery",
    p: "Validated leads land in your CRM in real time. Our technical capability extends to complex enterprise integrations, no matter the stack. Real-time delivery protects contact rates, leads arrive while the prospect's interest is still live. Your team spends their time on calls that convert, not follow-ups that go cold."
  }];

  return (
    <section className="hiw" id="how" data-screen-label="02 How it works">
      <div className="wrap">
        <div className="hiw-head">
          <div className="label-col">
            <span className="eyebrow"><span className="num">02</span>How it works</span>
            <h2 style={{ marginTop: 20 }}>This is how you get <span className="accent ink">sales-ready leads.</span></h2>
            <p className="body-md" style={{ marginTop: 18 }}>Most agencies are paid to run campaigns. With us, you only pay for the leads we deliver. By putting our skin in the game, we shift the risk from your budget to our performance, incentivising our team to deliver high-quality results, every single time. That’s the Upside.</p>
            <p className="body-md" style={{ marginTop: 18 }}>To achieve this, we built an ecosystem that continuously generates sales-ready leads through our four-stage marketing engine.</p>
            <a className="link-btn" href="#why" style={{ marginTop: 28 }}>See why it works <span className="arrow">↓</span></a>
          </div>
          <div className="stages">
            {stages.map((s, i) => <div className="stage" key={i}>
                <span className="stage-num"><b>{s.n}</b></span>
                <h3>{s.h}</h3>
                <p>{s.p}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}

/* ──────── why ──────── */

function WhyItWorks({ onCta }) {
  return (
    <section className="why" id="why" data-tone="light" data-screen-label="03 Why it works">
      <div className="wrap">
        <div className="why-head">
          <div>
            <span className="eyebrow"><span className="num">03</span>Why it works</span>
          </div>
          <div>
            <h2>Companies <span className="accent ink">serious about scale</span> find their way to us.</h2>
            <p className="body-md" style={{ marginTop: 20, fontSize: 16, lineHeight: 1.5, color: "var(--ink-soft)", maxWidth: "52ch" }}>
              Companies don't reach out because they need more leads. They reach out because they need predictable, scalable growth without building a large internal marketing operation. Most of them have already hit a ceiling with what they're running today, and are looking for an existing operation that can take them past it.
            </p>
          </div>
        </div>

        <div className="why-cards">
          <div className="why-card">
            <span className="why-card-num">| Full-stack Expertise</span>
            <h3>Global Subject Matter <span className="accent">Experts.</span></h3>
            <p>Our specialist teams across Meta, Google, TikTok, Microsoft, and native advertising work as one global operation. The partnership gives you access to the expertise and the market advantage that comes with it. Most companies don't have capability at this depth without significant ongoing investment.</p>
          </div>
          <div className="why-card">
            <span className="why-card-num">| No Risk</span>
            <h3>We Absorb the <span className="accent">Innovation Risk.</span></h3>
            <p>The digital landscape changes weekly. Algorithm shifts, new ad formats, new AI tools, and constantly evolving consumer behaviour. Keeping up means constant testing, and most of what gets tested doesn't work. We carry that cost. You only pay for the leads that come out the other side.</p>
          </div>
          <div className="why-card">
            <span className="why-card-num">| Proven Growth</span>
            <h3>Total Alignment of <span className="accent">Interests.</span></h3>
            <p>Most agency relationships pay for activity. We're paid for results, and only when those results actually arrive. Because we fund the advertising and the technology build ourselves, we're incentivised the same way you are: leads that turn into business. If you don't scale, we don't either.</p>
          </div>
        </div>

        <div className="why-quote">
          <div>
            <span className="why-quote-eyebrow">| In short</span>
          </div>
          <div>
            <blockquote>
              Inconsistent lead flow and unpredictable cost per lead are why most companies reach out. We replace both with fixed cost and a <span className="accent">$50M+ media operation</span> built to deliver, reliably.
            </blockquote>
            <div className="why-quote-foot">
              <button className="link-btn-primary" onClick={onCta}>
                Begin a partnership <span className="arrow">→</span>
              </button>
              <a className="link-btn" href="#team">See the team behind the performance <span className="arrow">↓</span></a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ──────── stats strip ──────── */

function useCounter(target, duration = 1300) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(target * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);
  return [ref, val] as const;
}

function StatNum({ target, prefix = "", suffix = "", decimals = 0 }) {
  const [ref, val] = useCounter(target);
  let display;
  if (decimals > 0) display = val.toFixed(decimals);else
  display = Math.floor(val).toLocaleString("en-AU");
  return <span ref={ref} className="stat-num">{prefix}{display}<span className="suf">{suffix}</span></span>;
}

function StatsStrip() {
  const items = [
  { num: 24, suffix: "+", lbl: "Countries operated in" },
  { num: 9.6, suffix: "M+", decimals: 1, lbl: "Annual ad interactions" },
  { num: 1.5, suffix: "M+", decimals: 1, lbl: "Validated leads each year" },
  { num: 100, suffix: "K+", lbl: "Original creatives annually" },
  { num: 20, suffix: "+", lbl: "Channel sources actively run" }];

  return (
    <section className="stats-strip" data-tone="dark" data-screen-label="04 Statistics">
      <div className="wrap">
        <div className="stats-grid">
          {items.map((it, i) =>
          <div className="stat-cell" key={i}>
              <StatNum target={it.num} suffix={it.suffix} decimals={it.decimals} />
              <span className="stat-lbl">{it.lbl}</span>
            </div>
          )}
        </div>
      </div>
    </section>);

}

/* ──────── partners ──────── */

function Partners() {
  const partners = [
    { name: "Google Ads", logo: R.pGoogle, tier: "Premier Partner" },
    { name: "Microsoft Advertising", logo: R.pMicrosoft, tier: "Elite Partner" },
    { name: "Meta Ads", logo: R.pMeta, tier: "Business Partner" },
    { name: "TikTok Ads", logo: R.pTikTok, tier: "Marketing Partner" },
    { name: "Taboola", logo: R.pTaboola, tier: "Marketing Partner" },
    { name: "Outbrain Direct Response", logo: R.pOutbrain, tier: "Marketing Partner" }];

  return (
    <section className="partners" data-tone="light" data-screen-label="05 Partners">
      <div className="wrap partners-head">
        <div>
          <span className="eyebrow"><span className="num">05</span>Trusted partners</span>
        </div>
        <div>
          <h2>Recognised across<br /><span className="accent ink">every major platform.</span></h2>
        </div>
      </div>
      <div className="wrap partners-container">
        <div className="partners-grid">
          {partners.map((p, i) =>
            <div className="pcell" key={i}>
              <div className="pcell-logo"><img src={p.logo} alt={p.name} /></div>
              <div className="pcell-tier">{p.tier}</div>
            </div>
          )}
        </div>
      </div>
    </section>);

}

/* ──────── leadership ──────── */

function Leadership() {
  return (
    <section className="lead-section" id="team" data-screen-label="06 Leadership">
      <div className="wrap">
        <div className="lead-grid">
          <div className="lead-image-wrap">
            <img src={R.leeRyan} alt="Lee Bush and Ryan Beck" />
            <div className="lead-image-cap lead-image-cap-left">
              <b>Lee Bush</b>Director
            </div>
            <div className="lead-image-cap lead-image-cap-right">
              <b>Ryan Beck</b>General Manager
            </div>
          </div>
          <div className="lead-text">
            <span className="eyebrow"><span className="num">06</span>The team</span>
            <h2>The people behind <span className="accent ink">the partnership.</span></h2>
            <p>Lee Bush founded Upside in 2009 and leads the technology and operating model. Her background in IT shaped the high-performance marketing engine that drives the business. She's at the centre of the strategy behind every partnership, engineering lead quality and unit economics into a model that scales.

            </p>
            <p>
              Ryan Beck leads operations and client partnerships. A long-standing voice in Australian digital, having spoken at industry events including Adtech Sydney and the National eCommerce Conferences. He's the driving force behind the operational growth of the business, and the reason working with us feels as good as the profit it produces for you.
            </p>
            <p>Together, Lee and Ryan lead a global team of specialists, supported by the technology, data science, and AI that's built to amplify results. Our operation spans every timezone, with performance optimisation running continuously, so every partnership benefits from profitable results. No risk to you. All upside.

            </p>
            <div className="lead-meta">
              <div className="lm"><b style={{ fontWeight: "500", color: "var(--red)" }}>17 YEARS</b><span>IN HIGH PERFORMANCE DIGITAL MARKETING</span></div>
              <div className="lm"><b style={{ fontWeight: "500", color: "var(--red)" }}>26 SPECIALISTS</b><span>MEDIA, CREATIVE, CONVERSION, DATA, & TECHNOLOGY
                </span></div>
              <div className="lm"><b style={{ fontWeight: "500", color: "var(--red)" }}>GLOBAL PRESENCE</b><span>AUSTRALIA (HQ), NORTH AMERICA, EUROPE, ASIA PACIFIC</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>);
}

/* ──────── closing ──────── */

function Closing({ ctaLabel, onCta }) {
  return (
    <section className="closing" data-tone="dark" data-screen-label="07 Closing CTA">
      <div className="wrap">
        <div className="closing-grid">
          <div>
            <span className="eyebrow"><span className="num">07</span>Next step</span>
          </div>
          <div>
            <h2>The right partnership <em>starts with the right fit.</em></h2>
            <p className="closing-side" style={{ marginTop: 28 }}>
              Every partnership is significant investment, on both sides. Start a conversation and we'll work out the fit together.
            </p>
            <div className="closing-foot">
              <button className="link-btn-primary" onClick={onCta}>
                {ctaLabel} <span className="arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

}

/* ──────── footer ──────── */

function HomePage() {
  const [csOpen, setCsOpen] = useState(false);

  useEffect(() => {document.body.setAttribute("data-density", "standard");}, []);

  const openCs = (e) => {if (e) e.preventDefault();setCsOpen(true);};

  return (
    <div className="page page-home">
      <Nav onCta={openCs} ctaLabel={CTA_LABEL} />
      <Hero headline={HEADLINE} ctaLabel={CTA_LABEL} onCta={openCs} />
      <HowItWorks />
      <WhyItWorks onCta={openCs} />
      <StatsStrip />
      <Partners />
      <Leadership />
      <Closing ctaLabel={CTA_LABEL} onCta={openCs} />
      <Footer onContact={openCs} />

      <ContactModal open={csOpen} onClose={() => setCsOpen(false)} />
    </div>);

}

export default HomePage;
